import { Button, VStack, useDisclosure } from '@chakra-ui/react';
import { Accordion } from 'Atoms';
import { Typography } from 'Tokens';
import { TargetIcon, TargetOffIcon } from 'Tokens/Icons/Data';
import { useGetAggregatedMetricsData } from '../Metrics';
import { IconHelpCircle } from '@tabler/icons-react';
import { LearnMoreDrawer } from 'Molecules';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  DREnums,
  FrameworkRefs,
  useAssessmentReportingUnits,
  useMaterialStandardId,
} from 'containers/Esrs';
import { Metrics } from '../Metrics/Metrics';
import Header from '../DRHeader/Header.tsx';
import { DrInfoHeader } from '../Metrics/Metrics/MetricsComponents/DrInfoHeader.tsx';
import { useCompanyType } from 'utils/hooks/General.hooks.ts';
import { useEsrsAssessmentProjectLeaderQuery } from 'models/index.ts';
import { GroupOrderOptions } from '../Metrics/AggregatedMetrics/AggregatedMetricsUtils.tsx';
import { TaskFilterOptions } from '../Metrics/MetricsTable/MetricsTable.utils.tsx';
import { useAuth } from 'Providers/auth/AuthProvider.tsx';

export const TargetDisclosureRequirement = ({
  setTaskFilter,
  setSelectedUserId,
  selectedUserId,
  taskFilter,
  selectedAdditionalTypes,
  groupOrOrder,
  setAdditionalTypesOptions,
}: {
  setTaskFilter: (val: TaskFilterOptions) => void;
  setSelectedUserId: (val: string) => void;
  taskFilter: TaskFilterOptions;
  selectedUserId?: string;
  groupOrOrder?: GroupOrderOptions;
  selectedAdditionalTypes?: string[];
  setMetricSidebarOpen: (val: boolean) => void;
  setAdditionalTypesOptions?: (
    s: {
      label: string;
      ref: string;
    }[]
  ) => void;
}) => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const {
    esrsAssessmentId = '',
    disclosureRequirementRef = '',
    standardRef = '',
    framework = '',
    reportingUnitId,
  } = useParams();
  const { companyAssessmentId, parentAssessmentId, parentEsrsAssessmentId } = useMaterialStandardId(
    standardRef,
    esrsAssessmentId
  );
  const { reportingUnitAssessments } = useAssessmentReportingUnits(esrsAssessmentId);
  const companyLevelReportingUnitId = useMemo(
    () => reportingUnitAssessments.find((ru) => ru.isCompanyLevel)?.id ?? '',
    [reportingUnitAssessments]
  );
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [accordionOpenIndex, setAccordionOpenIndex] = useState<number[]>([]);
  const urlTaskUserId = useMemo(() => searchParams.get('taskUserId'), [searchParams]);

  const { companyType } = useCompanyType();
  const isGroup = useMemo(() => companyType === 'group-company', [companyType]);

  const { data: assessmentData } = useEsrsAssessmentProjectLeaderQuery({
    variables: { esrsAssessmentId },
    skip: !esrsAssessmentId,
  });
  const projectLeader = useMemo(
    () => assessmentData?.esrsAssessment?.projectLeader ?? undefined,
    [assessmentData]
  );

  const { metrics: definedTargetMetrics } = useGetAggregatedMetricsData(
    false,
    companyAssessmentId,
    parentAssessmentId,
    false
  );
  const { metrics: notDefinedTargetMetrics } = useGetAggregatedMetricsData(
    true,
    companyAssessmentId,
    parentAssessmentId,
    false
  );

  useEffect(() => {
    if (urlTaskUserId) {
      setAccordionOpenIndex([0, 1]);
      if (urlTaskUserId === user?.id) setTaskFilter(TaskFilterOptions.yourTasks);
      else {
        setTaskFilter(TaskFilterOptions.members);
        setSelectedUserId(urlTaskUserId);
      }
    }
  }, [urlTaskUserId, user]);

  const accordionItems = [
    {
      id: 'defined',
      label: 'Disclosure requirements when targets are defined',
      icon: <TargetIcon mt="4px" />,
      content: (
        <Metrics
          noFilter={false}
          customTaskFilter={taskFilter}
          customGroupOrOrder={groupOrOrder}
          customSelectedMemberId={selectedUserId}
          customSelectedAdditionalTypes={selectedAdditionalTypes}
          setCustomTaskFilter={setTaskFilter}
          setAdditionalTypesOptions={setAdditionalTypesOptions}
        />
      ),
      extra: (
        <Button
          variant="ghost"
          leftIcon={<IconHelpCircle size="16px" />}
          fontWeight={500}
          onClick={(e) => {
            e.preventDefault();
            onOpen();
          }}
        >
          Learn more
        </Button>
      ),
    },
    {
      id: 'notDefined',
      label: (
        <Typography variant="h3" as={'p'}>
          Disclosure requirements when targets are
          <Typography variant="h3" as={'span'} color="text.critical">
            {' '}
            not defined
          </Typography>
        </Typography>
      ),
      icon: <TargetOffIcon color="border.critical.accent" mt="3px" />,
      content: (
        <Metrics
          withAssociation={true}
          noFilter={false}
          customTaskFilter={taskFilter}
          customSelectedAdditionalTypes={selectedAdditionalTypes}
          customGroupOrOrder={groupOrOrder}
          customSelectedMemberId={selectedUserId}
          setCustomTaskFilter={setTaskFilter}
          setAdditionalTypesOptions={setAdditionalTypesOptions}
        />
      ),
      extra: (
        <Button
          variant="ghost"
          leftIcon={<IconHelpCircle size="16px" />}
          fontWeight={500}
          onClick={(e) => {
            e.preventDefault();
            onOpen();
          }}
        >
          Learn more
        </Button>
      ),
    },
  ];

  const filteredAccordionItems = useMemo(() => {
    return accordionItems.filter((item) => {
      if (item.id === 'defined') {
        return (definedTargetMetrics ?? []).length > 0;
      }
      if (item.id === 'notDefined') {
        return (notDefinedTargetMetrics ?? []).length > 0;
      }
      return true;
    });
  }, [accordionItems, definedTargetMetrics, notDefinedTargetMetrics]);

  useEffect(() => {
    if (taskFilter !== TaskFilterOptions.allTasks) {
      setAccordionOpenIndex([0, 1]);
    }
  }, [taskFilter]);

  return (
    <VStack alignItems="start" w="100%" spacing="24px">
      <Header
        showInfobox={framework === FrameworkRefs.esrs}
        infoboxDescription="ESRS requires to disclose on different data points based when you do or don't have targets for each topic. Please answer the relevant data points below."
      />

      <DrInfoHeader
        drReference={disclosureRequirementRef}
        drType={DREnums.target}
        materialStandardId={companyAssessmentId}
        framework={framework}
        isBusinessUnit={false}
        isGroup={isGroup}
        standardRef={standardRef}
        esrsAssessmentId={esrsAssessmentId}
        parentAssessmentId={parentAssessmentId}
        projectLeader={projectLeader}
        reportingUnitId={reportingUnitId}
        companyReportingUnitId={companyLevelReportingUnitId}
        requiredDrMetrics={[...definedTargetMetrics, ...notDefinedTargetMetrics].map(
          (item) => item.reference
        )}
        parentEsrsAssessmentId={parentEsrsAssessmentId}
      />

      {framework === FrameworkRefs.esrs ? (
        <Accordion
          variant="solid"
          items={filteredAccordionItems}
          openIndex={accordionOpenIndex}
          setOpenIndex={setAccordionOpenIndex}
        />
      ) : (
        <>
          <Metrics setAdditionalTypesOptions={setAdditionalTypesOptions} />
        </>
      )}
      <LearnMoreDrawer
        isOpen={isOpen}
        onClose={onClose}
        header="Targets Disclosure Requirements"
        description='<div>
        <p>ESRS requires disclosure on different data points based on whether you have targets for this topic or not. Please answer the relevant data points in one of the groups on this page.</p>
        <br/>
        <p><span style="font-weight:600;" data-token-index="1" class="notion-enable-hover">Disclosure requirements when targets are defined</span></p>
        <p>Provide answers in this group if you have set or are planning to set targets for one or more of the material sustainability matters.</p>
        <br/>
        <p><span style="font-weight:600;" data-token-index="3" class="notion-enable-hover">Disclosure requirements when targets are not defined</span></p>
        <p>Provide answers in this group if you have not set or are not planning to set targets for one or more of the material sustainability matters.</p>
        </div>'
      />
    </VStack>
  );
};
